import { useAuthStore } from '@/stores/auth-store';
import { getCookie } from '@/lib/cookies';
import { getDeviceId } from '@/lib/device';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  cache?: RequestCache;
  token?: string;
}

/** Backend error response shape from AllExceptionsFilter */
export interface ApiErrorResponse {
  success: false;
  statusCode: number;
  message: string;
  errors?: Record<string, string[]>;
  timestamp: string;
}

class ApiError extends Error {
  constructor(
    public status: number,
    public data: ApiErrorResponse | Record<string, unknown>
  ) {
    super(`API Error: ${status}`);
    this.name = 'ApiError';
  }
}

const PUBLIC_PATHS = [
  'auth/login',
  'auth/login-with-code',
  'auth/register',
  'auth/send-code',
  'auth/refresh',
  'auth/magic-link/',
  'judge-certificates/public/',
];

/**
 * Parse a filename from a Content-Disposition header. Prefers the RFC 5987
 * `filename*=UTF-8''<pct-encoded>` form (correct for non-ASCII names like
 * 张三-ABC123.pdf), falls back to the legacy `filename="..."` form, then null.
 */
function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const starMatch = header.match(/filename\*=([^;]+)/i);
  if (starMatch) {
    const raw = starMatch[1].trim();
    const idx = raw.indexOf("''");
    const encoded = idx >= 0 ? raw.slice(idx + 2) : raw;
    try {
      return decodeURIComponent(encoded);
    } catch {
      return encoded;
    }
  }
  const plainMatch = header.match(/filename="?([^";]+)"?/i);
  return plainMatch ? plainMatch[1].trim() : null;
}

class ApiClient {
  private baseUrl: string;
  /** 正在进行的刷新操作，用于去重并发请求 */
  private pendingRefreshPromise: Promise<boolean> | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Check if the current access token is expiring within the next 2 minutes.
   * Returns false when expiry time is unknown (treat as valid, let 401 catch it).
   */
  private isTokenExpiringSoon(): boolean {
    const expiresAt = useAuthStore.getState().accessTokenExpiresAt;
    if (!expiresAt) return false;
    return Date.now() + 120_000 >= expiresAt;
  }

  /**
   * Ensure we have a valid access token before making a request.
   * If the token is about to expire, proactively refresh it.
   * Multiple concurrent calls share the same refresh promise.
   *
   * @returns The current valid access token, or null if no token is available.
   */
  /**
   * Ensure we have a valid access token before making a request.
   * If the token is about to expire, proactively refresh it.
   * Multiple concurrent calls share the same refresh promise via handleTokenRefresh().
   *
   * @returns The current valid access token, or null if no token is available.
   */
  private async ensureValidToken(): Promise<string | null> {
    const state = useAuthStore.getState();

    // No access token at all — let request fail with 401
    if (!state.accessToken) return null;

    // Token is still valid (not expiring soon) — use it as-is
    if (!this.isTokenExpiringSoon()) return state.accessToken;

    // Token is expiring but no refresh token — let 401 handle it
    if (!state.refreshToken) return state.accessToken;

    // Trigger deduplicated refresh (handles in-flight check internally)
    const success = await this.handleTokenRefresh();
    return success ? useAuthStore.getState().accessToken : null;
  }

  /**
   * Deduplicated entry point for token refresh.
   * Used by both proactive refresh (ensureValidToken) and 401 fallback handlers.
   */
  private async handleTokenRefresh(): Promise<boolean> {
    // If a refresh is already in-flight, share the same promise
    if (this.pendingRefreshPromise) {
      return this.pendingRefreshPromise;
    }

    this.pendingRefreshPromise = this.doRefresh().finally(() => {
      this.pendingRefreshPromise = null;
    });

    return this.pendingRefreshPromise;
  }

  private async doRefresh(): Promise<boolean> {
    const refreshToken = useAuthStore.getState().refreshToken;
    if (!refreshToken) return false;

    const deviceId = getDeviceId();

    try {
      const response = await fetch(`${this.baseUrl}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken, deviceId }),
      });

      if (!response.ok) {
        useAuthStore.getState().clear();
        return false;
      }

      const result = await response.json();
      // Backend wraps all responses in { success, data, timestamp }
      // Unwrap to get the actual data, but only if it looks like a wrapper
      let data;
      if (result && typeof result === 'object' && 'success' in result && 'data' in result && 'timestamp' in result) {
        data = result.data;
      } else {
        data = result;
      }
      // Keep the existing rememberMe preference when refreshing
      const currentRememberMe = useAuthStore.getState().rememberMe;
      useAuthStore.getState().setTokensWithRemember(data.accessToken, data.refreshToken, currentRememberMe);
      return true;
    } catch {
      useAuthStore.getState().clear();
      return false;
    }
  }

  async request<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    const { method = 'GET', body, headers = {}, cache, token } = options;

    const url = `${this.baseUrl}/api/${endpoint.replace(/^\/+/, '')}`;

    const normalizedEndpoint = endpoint.replace(/^\/+/, '');
    const isPublicPath = PUBLIC_PATHS.some(p => normalizedEndpoint.startsWith(p));

    // Proactive refresh: ensure token is valid before non-public requests
    let accessToken: string | null = null;
    if (!isPublicPath && !token) {
      accessToken = await this.ensureValidToken();
    }

    // Fall back to store/cookie if ensureValidToken returned null
    if (!accessToken) {
      const storeToken = useAuthStore.getState().accessToken;
      const cookieToken = getCookie('accessToken');
      accessToken = token ?? storeToken ?? cookieToken;
    }

    const fetchOptions: RequestInit = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...headers,
      },
    };

    if (body) {
      fetchOptions.body = JSON.stringify(body);
    }

    // Add cache support for React Query
    if (cache) {
      fetchOptions.cache = cache;
    }

    const response = await fetch(url, fetchOptions);

    // Handle 401 - try token refresh (skip for public paths)
    if (response.status === 401) {
      const normalizedEndpoint = endpoint.replace(/^\/+/, '');
      const isPublicPath = PUBLIC_PATHS.some(p => normalizedEndpoint.startsWith(p));

      if (isPublicPath) {
        const error = await response.json().catch(() => ({}));
        throw new ApiError(response.status, error);
      }

      const refreshed = await this.handleTokenRefresh();
      if (refreshed) {
        // Retry with new token - DON'T spread original options.headers
        // because it may contain the old Authorization header
        const newToken = useAuthStore.getState().accessToken;
        if (newToken) {
          // Create new options without the old headers' Authorization
          const { headers: _, ...restOptions } = options;
          return this.request<T>(endpoint, {
            ...restOptions,
            token: newToken,
          });
        }
      }
      // Redirect to login if refresh failed, preserving current path for post-login redirect
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname + window.location.search;
        const loginUrl = currentPath.startsWith('/login')
          ? '/login'
          : `/login?redirect=${encodeURIComponent(currentPath)}`;
        window.location.href = loginUrl;
      }
      throw new ApiError(401, { message: 'Unauthorized' });
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new ApiError(response.status, error);
    }

    const result = await response.json();
    // Backend wraps all responses in { success, data, timestamp }
    // Unwrap to get the actual data, but only if it looks like a wrapper
    if (result && typeof result === 'object' && 'success' in result && 'data' in result && 'timestamp' in result) {
      return result.data as T;
    }
    return result as T;
  }

  get<T>(endpoint: string, options?: Omit<ApiOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  post<T>(endpoint: string, body: unknown, options?: Omit<ApiOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body });
  }

  put<T>(endpoint: string, body: unknown, options?: Omit<ApiOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body });
  }

  patch<T>(endpoint: string, body: unknown, options?: Omit<ApiOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body });
  }

  delete<T>(endpoint: string, body?: unknown, options?: Omit<ApiOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE', body });
  }

  async downloadBlob(endpoint: string, options?: Omit<ApiOptions, 'method'> & { method?: 'GET' | 'POST' }): Promise<Blob> {
    const url = `${this.baseUrl}/api/${endpoint.replace(/^\/+/, '')}`;

    // Proactive refresh
    let accessToken = await this.ensureValidToken();
    if (!accessToken) {
      const storeToken = useAuthStore.getState().accessToken;
      const cookieToken = getCookie('accessToken');
      accessToken = options?.token ?? storeToken ?? cookieToken;
    }

    const method = options?.method ?? 'GET';
    const fetchOptions: RequestInit = {
      method,
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(options?.body ? { 'Content-Type': 'application/json' } : {}),
        ...options?.headers,
      },
      ...(options?.body ? { body: JSON.stringify(options.body) } : {}),
    };

    const response = await fetch(url, fetchOptions);

    // Handle 401 - try token refresh
    if (response.status === 401) {
      const refreshed = await this.handleTokenRefresh();
      if (refreshed) {
        const newToken = useAuthStore.getState().accessToken;
        if (newToken) {
          return this.downloadBlob(endpoint, { ...options, token: newToken });
        }
      }
      // Redirect to login if refresh failed, preserving current path for post-login redirect
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname + window.location.search;
        const loginUrl = currentPath.startsWith('/login')
          ? '/login'
          : `/login?redirect=${encodeURIComponent(currentPath)}`;
        window.location.href = loginUrl;
      }
      throw new ApiError(401, { message: 'Unauthorized' });
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new ApiError(response.status, error);
    }

    return response.blob();
  }

  /**
   * Download a binary attachment and return its blob + the filename parsed
   * from the Content-Disposition header (RFC 5987 aware). Auth / token-refresh
   * handling mirrors downloadBlob. Use this instead of downloadBlob when the
   * client needs the real (possibly non-ASCII) filename from the server —
   * e.g. certificate downloads whose filename embeds the recipient's name.
   */
  async downloadFile(
    endpoint: string,
    options?: Omit<ApiOptions, 'method'> & { method?: 'GET' | 'POST' },
  ): Promise<{ blob: Blob; filename: string | null }> {
    const url = `${this.baseUrl}/api/${endpoint.replace(/^\/+/, '')}`;

    // Proactive refresh
    let accessToken = await this.ensureValidToken();
    if (!accessToken) {
      const storeToken = useAuthStore.getState().accessToken;
      const cookieToken = getCookie('accessToken');
      accessToken = options?.token ?? storeToken ?? cookieToken;
    }

    const method = options?.method ?? 'GET';
    const fetchOptions: RequestInit = {
      method,
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(options?.body ? { 'Content-Type': 'application/json' } : {}),
        ...options?.headers,
      },
      ...(options?.body ? { body: JSON.stringify(options.body) } : {}),
    };

    const response = await fetch(url, fetchOptions);

    // Handle 401 - try token refresh
    if (response.status === 401) {
      const refreshed = await this.handleTokenRefresh();
      if (refreshed) {
        const newToken = useAuthStore.getState().accessToken;
        if (newToken) {
          return this.downloadFile(endpoint, { ...options, token: newToken });
        }
      }
      // Redirect to login if refresh failed, preserving current path for post-login redirect
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname + window.location.search;
        const loginUrl = currentPath.startsWith('/login')
          ? '/login'
          : `/login?redirect=${encodeURIComponent(currentPath)}`;
        window.location.href = loginUrl;
      }
      throw new ApiError(401, { message: 'Unauthorized' });
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new ApiError(response.status, error);
    }

    const filename = parseContentDispositionFilename(
      response.headers.get('Content-Disposition'),
    );
    const blob = await response.blob();
    return { blob, filename };
  }

  async upload<T>(endpoint: string, formData: FormData, options?: Omit<ApiOptions, 'method' | 'body'>): Promise<T> {
    const url = `${this.baseUrl}/api/${endpoint.replace(/^\/+/, '')}`;

    // Proactive refresh
    let accessToken = await this.ensureValidToken();
    if (!accessToken) {
      const storeToken = useAuthStore.getState().accessToken;
      const cookieToken = getCookie('accessToken');
      accessToken = options?.token ?? storeToken ?? cookieToken;
    }

    const fetchOptions: RequestInit = {
      method: 'POST',
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: formData,
    };

    const response = await fetch(url, fetchOptions);

    if (response.status === 401) {
      const refreshed = await this.handleTokenRefresh();
      if (refreshed) {
        const newToken = useAuthStore.getState().accessToken;
        if (newToken) return this.upload<T>(endpoint, formData, { token: newToken });
      }
      if (typeof window !== 'undefined') {
        const currentPath = window.location.pathname + window.location.search;
        const loginUrl = currentPath.startsWith('/login')
          ? '/login'
          : `/login?redirect=${encodeURIComponent(currentPath)}`;
        window.location.href = loginUrl;
      }
      throw new ApiError(401, { message: 'Unauthorized' });
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new ApiError(response.status, error);
    }

    const result = await response.json();
    if (result && typeof result === 'object' && 'success' in result && 'data' in result && 'timestamp' in result) {
      return result.data as T;
    }
    return result as T;
  }

  /**
   * Public token-refresh entry for non-`request` channels — e.g. the XHR
   * upload path in `uploadApi.uploadViaStream`, which can't reuse `request`'s
   * built-in 401 handling. Deduplicates concurrent refreshes via
   * handleTokenRefresh(). Returns the new access token, or null on failure.
   */
  async refreshAccessToken(): Promise<string | null> {
    const ok = await this.handleTokenRefresh();
    return ok ? useAuthStore.getState().accessToken : null;
  }
}

export const apiClient = new ApiClient();
export { ApiError };
export type { ApiOptions };
